<script setup lang="ts">
  import { computed, reactive, ref, watch, watchEffect } from 'vue'
  import { DataCleaningService } from '../services/DataCleaningService'
  import { Button, message } from 'ant-design-vue'
  import RuleConfiguration from './RuleConfiguration.vue'
  import { Trash2 } from 'lucide-vue-next'
  import {
    PlusOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    HolderOutlined,
    CompressOutlined,
    ExpandOutlined
  } from '@ant-design/icons-vue'
  import { defaultMessageDurationSeconds } from '@/utils'
  import RuleSelectionModal from './RuleSelectionModal.vue'
  import { RuleSchema } from '../models/RuleSchema'
  import { RuleField } from '../models/RuleField'
  import { Rule } from '../models/Rule'
  import { RuleFieldType } from '../models/RuleFieldType'
  import { AvailableColumn } from '../models/AvailableColumn'

  interface IArrayRuleBuilderProps {
    rulesSchemas: Array<RuleSchema>
    availableColumns: Array<AvailableColumn>
    ruleField: RuleField
    modelValue: []
    configurationLevel: number
    isCondensedLayout: boolean
    globalCollapsed?: { collapsed: boolean }
  }

  interface IArrayRuleBuilderEmits {
    // We have setup reactivity to auto-update based on rules array model changes
    (e: 'update:modelValue', value: Array<any>): void
    // Here we are just accounting for type change notification
    (e: 'child-rule-type-changed', type: string, rule: Rule, configuration: {}): void
  }

  const props = defineProps<IArrayRuleBuilderProps>()
  const emit = defineEmits<IArrayRuleBuilderEmits>()

  const arrayRuleField = reactive<RuleField>(props.ruleField)

  if (arrayRuleField.field_type !== RuleFieldType.Array)
    throw new Error('ArrayRuleBuilder requires an Array type RuleField!')
  if (arrayRuleField.item_schema == null)
    throw new Error('Array type RuleFields require an item_schema!')

  // The actual configured array of rules
  const rules = ref<Array<Rule>>([])
  const showRuleSelection = ref(false)
  const isCollapsed = ref<boolean>(false)
  const globalCollapsed = ref<{ collapsed: boolean }>(props.globalCollapsed ?? { collapsed: false })

  watchEffect(() => {
    const newVal = props.globalCollapsed
    if (newVal) {
      globalCollapsed.value = newVal
      isCollapsed.value = !!newVal.collapsed
    }
  })

  const toggleGlobalCollapse = () => {
    globalCollapsed.value = { collapsed: !isCollapsed.value }
    isCollapsed.value = !isCollapsed.value
  }
  // Drag/drop state
  const draggedIndex = ref<number | null>(null)
  const dragOverIndex = ref<number | null>(-1)
  const instanceId = `arrayRule-${Math.random().toString(36).slice(2)}`

  watch(
    props.modelValue,
    (newVal) => {
      if (Array.isArray(newVal)) {
        rules.value = []
        newVal.forEach((value: any) => {
          if (arrayRuleField.item_schema!.field_type === RuleFieldType.Rule) {
            const ruleSchema = DataCleaningService.tryGetRuleSchemaByType(
              value.type,
              props.rulesSchemas
            )
            if (ruleSchema != null) {
              const rule = DataCleaningService.createRuleFromRuleSchema(ruleSchema)
              rule.configuration =
                value.configuration != null
                  ? DataCleaningService.getFlattenedRule(value, null)
                  : value
              rules.value.push(rule)
            }
          } else {
            const rule = DataCleaningService.createRuleFromRuleField(arrayRuleField.item_schema!)
            rule.configuration =
              value.configuration != null
                ? DataCleaningService.getFlattenedRule(value, null)
                : value
            rules.value.push(rule)
            //rule.configuration.type = rule.type
          }
        })
      }
    },
    { immediate: true, deep: true }
  )

  watch(
    rules,
    (newValue) => {
      emit('update:modelValue', newValue)
    },
    { deep: true }
  )

  // Initialize configuration with default values
  const initializeConfiguration = (rule: Rule, fields: Array<RuleField>) => {
    if (rule == null) throw new Error('rule is required')
    if (arrayRuleField.item_schema?.field_type !== RuleFieldType.Object) return
    const config = DataCleaningService.initializeRuleFieldConfiguration(fields)
    rule.configuration = config
  }

  const isAddDisabled = computed(() => {
    const maxItems = (arrayRuleField.field_type_args as any)?.array_max_items
    if (maxItems === undefined || maxItems === null) return false
    if (typeof maxItems !== 'number' || isNaN(maxItems) || maxItems < 0) return false
    return rules.value.length >= maxItems
  })

  const addRule = () => {
    const isArrayOfRuleObjects = arrayRuleField.item_schema?.field_type === RuleFieldType.Rule
    if (isArrayOfRuleObjects) {
      // Create new rule from rule selection modal
      showRuleSelection.value = true
    } else {
      // Create new rule from item_schema
      const newRule = DataCleaningService.createRuleFromRuleField(arrayRuleField.item_schema!)
      initializeConfiguration(newRule, arrayRuleField.item_schema?.fields || [])
      rules.value.push(newRule)
      message.success(`Added rule: ${arrayRuleField.display_name}`, defaultMessageDurationSeconds)
      return newRule
    }
  }

  const onRuleSelected = (ruleSchema: RuleSchema): Rule => {
    const newRule = DataCleaningService.createRuleFromRuleSchema(ruleSchema)
    rules.value.push(newRule)
    message.success(`Added rule: ${ruleSchema.display_name}`, defaultMessageDurationSeconds)
    return newRule
  }

  const removeRule = (index: number) => {
    rules.value.splice(index, 1)
  }

  const moveItem = (from: number, to: number) => {
    if (from === to) return
    const item = rules.value.splice(from, 1)[0]
    rules.value.splice(to, 0, item)
  }

  const moveUp = (index: number) => {
    if (index > 0) {
      moveItem(index, index - 1)
    }
  }

  const moveDown = (index: number) => {
    if (index < rules.value.length - 1) {
      moveItem(index, index + 1)
    }
  }

  const handleDragLeave = () => {
    dragOverIndex.value = null
  }

  const handleDragStart = (event: DragEvent, index: number) => {
    event.stopPropagation()
    draggedIndex.value = index
    const id = instanceId
    // store origin in dataTransfer and window fallback
    try {
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move'
        event.dataTransfer.setData('text/plain', String(index))
        event.dataTransfer.setData('application/x-array-builder-id', id)
      }
    } catch {}
    ;(window as any).__arrayBuilderDragSourceId = id
  }

  const handleDragOver = (event: DragEvent, index: number) => {
    event.preventDefault()
    event.stopPropagation()
    let sourceId: string | null = null
    try {
      sourceId = event.dataTransfer?.getData('application/x-array-builder-id') || null
    } catch {}
    sourceId = sourceId || (window as any).__arrayBuilderDragSourceId || null
    if (sourceId !== instanceId) {
      dragOverIndex.value = -1
      return
    }
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move'
    dragOverIndex.value = index
  }

  const handleDrop = (event: DragEvent, targetIndex: number) => {
    event.preventDefault()
    event.stopPropagation()
    let sourceId: string | null = null
    try {
      sourceId = event.dataTransfer?.getData('application/x-array-builder-id') || null
    } catch {}
    sourceId = sourceId || (window as any).__arrayBuilderDragSourceId || null
    if (sourceId !== instanceId) return

    if (draggedIndex.value == null) return
    const from = draggedIndex.value
    const to = targetIndex
    if (from === to) {
      draggedIndex.value = null
      dragOverIndex.value = -1
      ;(window as any).__arrayBuilderDragSourceId = null
      return
    }
    moveItem(from, to)
    draggedIndex.value = null
    dragOverIndex.value = -1
    ;(window as any).__arrayBuilderDragSourceId = null
  }

  const handleDragEnd = () => {
    draggedIndex.value = null
    dragOverIndex.value = -1
    ;(window as any).__arrayBuilderDragSourceId = null
  }

  const handleConfigurationUpdate = (
    rule: Rule,
    config: Record<string, any>,
    ruleTypeChange?: boolean
  ) => {
    const ruleIndex = rules.value.findIndex((r) => r.id === rule.id)
    if (ruleIndex > -1) {
      rules.value[ruleIndex].configuration = { ...config }
    }
    if (ruleTypeChange === true) {
      emit('child-rule-type-changed', rule.type, rule, { ...config })
    }
  }
</script>

<template>
  <div
    v-if="
      arrayRuleField.item_schema?.field_type === RuleFieldType.Object ||
      arrayRuleField.item_schema?.field_type === RuleFieldType.Rule
    "
    class="array-object-builder-container"
  >
    <div class="mt-[-40px] self-end flex items-center gap-2">
      <a-tooltip placement="top">
        <template #title>{{ isCollapsed ? 'Expand All' : 'Collapse All' }}</template>
        <Button
          style="padding: 3px; border: none"
          type="text"
          size="large"
          class="flex items-center justify-center"
          @click="toggleGlobalCollapse"
          :disabled="rules.length === 0"
          :aria-disabled="rules.length === 0"
          aria-label="Toggle Collapse All"
        >
          <CompressOutlined v-if="isCollapsed" class="text-base" style="line-height: 1; margin: 0" />
          <ExpandOutlined v-else class="text-base" style="line-height: 1; margin: 0" />
        </Button>
      </a-tooltip>

      <Button
        type="primary"
        shape="circle"
        size="large"
        class="flex items-center justify-center"
        @click="addRule"
        :title="`Add ${arrayRuleField.item_schema!.display_name}`"
        :disabled="isAddDisabled"
        :aria-disabled="isAddDisabled"
      >
        <PlusOutlined class="text-base" style="line-height: 1; margin: 0" />
      </Button>
    </div>

    <div class="mt-5">
      <div
        class="mapping-row"
        v-for="(rule, index) in rules"
        :key="rule.id"
        :class="{ 'drop-zone-active': dragOverIndex === index }"
        @dragover.prevent="handleDragOver($event, index)"
        @dragleave="handleDragLeave"
        @drop.prevent="handleDrop($event, index)"
      >
        <div
          class="drag-handle"
          draggable="true"
          @dragstart="handleDragStart($event, index)"
          @dragend="handleDragEnd"
          tabindex="0"
          role="button"
          :aria-grabbed="draggedIndex === index"
          aria-label="Drag to reorder"
        >
          <HolderOutlined class="text-base" style="line-height: 1; margin: 0" />
        </div>

        <RuleConfiguration
          :configuration-level="props.configurationLevel + 1"
          :rule="rule"
          :available-columns="availableColumns"
          @configuration-updated="handleConfigurationUpdate"
          :rules-schemas="rulesSchemas"
          :is-condensed-layout="props.isCondensedLayout"
          :global-collapsed="globalCollapsed"
        />

        <div class="action-buttons ml-2 flex flex-col items-center">
          <a-tooltip placement="left">
            <template #title>Move Up</template>
            <Button
              type="default"
              shape="circle"
              size="small"
              class="compact-action-btn"
              @click="moveUp(index)"
              :disabled="index === 0"
              :aria-disabled="index === 0"
            >
              <ArrowUpOutlined class="compact-icon" style="line-height: 1; margin: 0" />
            </Button>
          </a-tooltip>

          <a-tooltip placement="left">
            <template #title>{{ `Remove ${arrayRuleField.item_schema!.display_name}` }}</template>
            <Button
              type="default"
              shape="circle"
              size="small"
              class="compact-action-btn ant-btn-dangerous my-1"
              @click="removeRule(index)"
            >
              <Trash2 class="compact-icon" style="line-height: 1; margin: 0" />
            </Button>
          </a-tooltip>

          <a-tooltip placement="left">
            <template #title>Move Down</template>
            <Button
              type="default"
              shape="circle"
              size="small"
              class="compact-action-btn"
              @click="moveDown(index)"
              :disabled="index === rules.length - 1"
              :aria-disabled="index === rules.length - 1"
            >
              <ArrowDownOutlined class="compact-icon" style="line-height: 1; margin: 0" />
            </Button>
          </a-tooltip>
        </div>
      </div>
    </div>
  </div>

  <RuleSelectionModal
    v-if="arrayRuleField.item_schema?.field_type === RuleFieldType.Rule"
    v-bind:configuration-level="props.configurationLevel"
    v-model:visible="showRuleSelection"
    :rules-schemas="rulesSchemas"
    @rule-selected="onRuleSelected"
  />
</template>

<style scoped>
  .array-object-builder-container {
    display: flex;
    flex-direction: column;
  }

  .mapping-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 15px;
  }

  .drag-handle {
    cursor: grab;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: transparent;
    color: var(--muted-foreground, #6b7280);
  }

  .mapping-row.drop-zone-active {
    outline: 2px dashed rgba(24, 144, 255, 0.35);
    background-color: rgba(24, 144, 255, 0.03);
  }

  .action-buttons .ant-btn {
    padding: 3px;
  }

  .compact-action-btn {
    width: 28px;
    height: 28px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .compact-action-btn.compact-action-btn.ant-btn-dangerous {
    padding: 0;
  }

  .compact-icon {
    font-size: 12px;
  }

  /* Ensure SVG-based icons (lucide) respect compact sizing */
  .compact-icon,
  .compact-icon svg {
    width: 14px !important;
    height: 14px !important;
    line-height: 1 !important;
  }

  select {
    padding: 4px;
  }
</style>
